import { useEffect, useRef, useState } from 'react';
import { api } from '../../../api/client.js';
import { ConfirmDialog } from '../../../components/ConfirmDialog.jsx';
import { ImageUploadField } from '../../../components/forms/ImageUploadField.jsx';
import { Modal } from '../../../components/Modal.jsx';
import { ItemFormModal } from './ItemFormModal.jsx';
import { moveId } from './reorderHelpers.js';
import { formatAdminPrice } from '../../../utils/currency.js';

function formatPrice(value, currency) {
  return formatAdminPrice(value, currency);
}

function Icon({ name, size = 17 }) {
  const paths = {
    plus: <path d="M12 5v14M5 12h14" />,
    edit: <><path d="M12 20h9" /><path d="m16.5 3.5 4 4L9 19l-5 1 1-5Z" /></>,
    up: <path d="m6 15 6-6 6 6" />,
    down: <path d="m6 9 6 6 6-6" />,
    trash: <><path d="M4 7h16M9 7V4h6v3M7 7l1 13h8l1-13M10 11v5M14 11v5" /></>,
    image: <><rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="9" cy="10" r="2" /><path d="m21 15-4-4L6 20" /></>,
  };
  return <svg className="ui-icon" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[name]}</svg>;
}

export function CategoryEditor({ menuId, category, currency, onReorderCategory, categoryReordering = false, isFirst, isLast, onChanged }) {
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(category.name);
  const [showSectionActions, setShowSectionActions] = useState(false);
  const [confirmDeleteCategory, setConfirmDeleteCategory] = useState(false);
  const [editingItemId, setEditingItemId] = useState(undefined);
  const [deleteItemTarget, setDeleteItemTarget] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [editingCover, setEditingCover] = useState(false);
  const [coverImage, setCoverImage] = useState(null);
  const [coverError, setCoverError] = useState(null);
  const [savingCover, setSavingCover] = useState(false);
  const [renamingBusy, setRenamingBusy] = useState(false);
  const [reorderingItem, setReorderingItem] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const actionsRef = useRef(null);
  const categoryBase = `/api/menus/${menuId}/categories/${category.id}`;
  const itemIds = category.items.map((item) => item.id);
  const editingItem = editingItemId ? category.items.find((item) => item.id === editingItemId) : null;

  useEffect(() => {
    if (!showSectionActions) return undefined;
    function closeActions(event) {
      if (event.type === 'keydown' && event.key !== 'Escape') return;
      if (event.type === 'pointerdown' && actionsRef.current?.contains(event.target)) return;
      setShowSectionActions(false);
    }
    document.addEventListener('keydown', closeActions);
    document.addEventListener('pointerdown', closeActions);
    return () => {
      document.removeEventListener('keydown', closeActions);
      document.removeEventListener('pointerdown', closeActions);
    };
  }, [showSectionActions]);

  async function handleRename(event) {
    event.preventDefault();
    if (!name.trim()) return;
    setActionError(null);
    setRenamingBusy(true);
    try {
      await api.patch(categoryBase, { name: name.trim() });
      setRenaming(false);
      onChanged();
    } catch (err) {
      setActionError(err.message);
    } finally {
      setRenamingBusy(false);
    }
  }
  function openCoverEditor() {
    setCoverImage(category.imageMediaId && category.imageUrl
      ? { id: category.imageMediaId, url: category.imageUrl }
      : null);
    setCoverError(null);
    setEditingCover(true);
  }
  async function handleSaveCover() {
    setCoverError(null);
    setSavingCover(true);
    try {
      await api.patch(categoryBase, { imageMediaId: coverImage?.id ?? null });
      setEditingCover(false);
      onChanged();
    } catch (err) {
      setCoverError(err.message);
    } finally {
      setSavingCover(false);
    }
  }
  async function handleDeleteCategory() {
    setActionError(null);
    setDeleting(true);
    try { await api.delete(categoryBase); setConfirmDeleteCategory(false); onChanged(); }
    catch (err) { setActionError(err.message); }
    finally { setDeleting(false); }
  }
  async function handleDeleteItem() {
    setActionError(null);
    setDeleting(true);
    try { await api.delete(`${categoryBase}/items/${deleteItemTarget.id}`); setDeleteItemTarget(null); onChanged(); }
    catch (err) { setActionError(err.message); }
    finally { setDeleting(false); }
  }
  async function handleReorderItem(itemId, direction) {
    const orderedIds = moveId(itemIds, itemId, direction);
    if (orderedIds === itemIds) return;
    setActionError(null);
    setReorderingItem(true);
    try {
      await api.post(`${categoryBase}/items/reorder`, { orderedIds });
      onChanged();
    } catch (err) {
      setActionError(err.message);
    } finally {
      setReorderingItem(false);
    }
  }

  return (
    <section className="menu-section" id={`category-${category.id}`}>
      <div className="menu-section__heading">
        {renaming ? (
          <form onSubmit={handleRename} className="menu-section__rename">
            <input aria-label="Section name" type="text" value={name} onChange={(event) => setName(event.target.value)} autoFocus />
            <button type="submit" className="btn btn-sm btn-primary" disabled={renamingBusy}>{renamingBusy ? 'Saving…' : 'Save'}</button>
            <button type="button" className="btn btn-sm btn-ghost" disabled={renamingBusy} onClick={() => { setName(category.name); setRenaming(false); }}>Cancel</button>
          </form>
        ) : (
          <>
            <div><span className="menu-section__kicker">Section</span><h3>{category.name}</h3></div>
            <div className="menu-section__actions" ref={actionsRef}>
              <button type="button" className="icon-btn" onClick={() => setRenaming(true)} aria-label={`Rename ${category.name}`} title="Rename section"><Icon name="edit" /></button>
              <button type="button" className="section-actions-toggle" onClick={() => setShowSectionActions((value) => !value)} aria-expanded={showSectionActions}>Organize <span aria-hidden="true">⌄</span></button>
              {showSectionActions && (
                <div className="section-actions-menu">
                  <button type="button" onClick={() => { openCoverEditor(); setShowSectionActions(false); }}><Icon name="image" /> Edit section cover</button>
                  <button type="button" onClick={() => { onReorderCategory(category.id, -1); setShowSectionActions(false); }} disabled={isFirst || categoryReordering}><Icon name="up" /> Move section up</button>
                  <button type="button" onClick={() => { onReorderCategory(category.id, 1); setShowSectionActions(false); }} disabled={isLast || categoryReordering}><Icon name="down" /> Move section down</button>
                  <button type="button" className="danger" onClick={() => { setActionError(null); setConfirmDeleteCategory(true); setShowSectionActions(false); }}><Icon name="trash" /> Delete section</button>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {actionError && <p className="field-error" role="alert">{actionError}</p>}

      {category.items.length === 0 ? (
        <button type="button" className="empty-menu-section" onClick={() => setEditingItemId(null)}>
          <span className="empty-menu-section__icon"><Icon name="plus" /></span><strong>Add the first item to {category.name}</strong><span>Give it a name, price, description, and optional photo.</span>
        </button>
      ) : (
        <div className="menu-items">
          {category.items.map((item, index) => (
            <article key={item.id} className={`menu-item-row${item.hidden ? ' is-hidden' : ''}`}>
              {item.imageUrl ? <img className="menu-item-row__image" src={item.imageUrl} alt="" onError={(event) => { event.currentTarget.hidden = true; }} /> : <div className="menu-item-row__image-placeholder" aria-hidden="true"><Icon name="image" /></div>}
              <button type="button" className="menu-item-row__content" onClick={() => setEditingItemId(item.id)} aria-label={`Edit ${item.name}`}>
                <span className="menu-item-row__title-line"><strong>{item.name}</strong>{item.isFeatured && <span className="menu-item-row__popular">Popular</span>}{item.hidden && <span className="badge badge-muted">Hidden</span>}</span>
                {item.description ? <span className="menu-item-row__description">{item.description}</span> : <span className="menu-item-row__description is-placeholder">Add a short description</span>}
                {(item.attributes.length > 0 || item.options.length > 0) && <span className="menu-item-row__meta">{item.attributes.slice(0, 3).map((attribute) => <span key={attribute.id}>{attribute.value}</span>)}{item.options.length > 0 && <span>{item.options.length} {item.options.length === 1 ? 'choice' : 'choices'}</span>}</span>}
              </button>
              <div className="menu-item-row__price">{formatPrice(item.price, currency) ?? <span>Price by choice</span>}</div>
              <div className="menu-item-row__controls">
                <button type="button" className="icon-btn" onClick={() => handleReorderItem(item.id, -1)} disabled={index === 0 || reorderingItem} aria-label={`Move ${item.name} up`} title="Move up"><Icon name="up" /></button>
                <button type="button" className="icon-btn" onClick={() => handleReorderItem(item.id, 1)} disabled={index === category.items.length - 1 || reorderingItem} aria-label={`Move ${item.name} down`} title="Move down"><Icon name="down" /></button>
                <button type="button" className="icon-btn" onClick={() => setEditingItemId(item.id)} aria-label={`Edit ${item.name}`} title="Edit item"><Icon name="edit" /></button>
                <button type="button" className="icon-btn icon-btn--danger" onClick={() => { setActionError(null); setDeleteItemTarget(item); }} aria-label={`Delete ${item.name}`} title="Delete item"><Icon name="trash" /></button>
              </div>
            </article>
          ))}
        </div>
      )}

      {category.items.length > 0 && <button type="button" className="menu-section__add-item" onClick={() => setEditingItemId(null)}><Icon name="plus" /> Add an item to {category.name}</button>}
      {editingItemId !== undefined && <ItemFormModal menuId={menuId} categoryId={category.id} categoryName={category.name} currency={currency} item={editingItem} onClose={() => setEditingItemId(undefined)} onSaved={onChanged} />}
      {editingCover && (
        <Modal onClose={() => setEditingCover(false)} labelledBy={`section-cover-title-${category.id}`} busy={savingCover}>
          <h2 id={`section-cover-title-${category.id}`}>Section cover image</h2>
          <p className="modal-description">This image appears above the {category.name} section and is separate from its dish photos.</p>
          {coverError && <p className="field-error" role="alert">{coverError}</p>}
          <ImageUploadField
            label="Cover image"
            kind="CATEGORY_IMAGE"
            value={coverImage}
            onChange={setCoverImage}
            hint="A wide image works best; the menu crops it into a short banner."
          />
          <div className="choice-group-form__actions">
            <button type="button" className="btn btn-ghost" onClick={() => setEditingCover(false)} disabled={savingCover}>Cancel</button>
            <button type="button" className="btn btn-primary" onClick={handleSaveCover} disabled={savingCover}>{savingCover ? 'Saving…' : 'Save cover'}</button>
          </div>
        </Modal>
      )}
      {confirmDeleteCategory && <ConfirmDialog title={`Delete “${category.name}”?`} description="This also removes every item in this section." confirmLabel="Delete section" busy={deleting} error={actionError} onConfirm={handleDeleteCategory} onCancel={() => setConfirmDeleteCategory(false)} />}
      {deleteItemTarget && <ConfirmDialog title={`Delete “${deleteItemTarget.name}”?`} description="This item will be removed from your menu." confirmLabel="Delete item" busy={deleting} error={actionError} onConfirm={handleDeleteItem} onCancel={() => setDeleteItemTarget(null)} />}
    </section>
  );
}
